import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  User,
  Phone,
  Mail,
  Briefcase,
  UploadCloud,
  CheckCircle,
  MessageSquare,
} from "lucide-react";

import { validateField, validateForm } from "../utils/workWithUsValidation";

import { sendWorkWithUsApplication } from "../services/workWithUsService";

import Swal from "sweetalert2";

const WorkWithUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    position: "",
    message: "",
    file: null,
  });

  const [errors, setErrors] = useState({});

  const [isSending, setIsSending] = useState(false);

  const [submitMessage, setSubmitMessage] = useState({
    type: "",
    text: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;

    let newValue = value;

    // Solo permitir números en teléfono
    if (name === "phone") {
      newValue = value.replace(/\D/g, "").slice(0, 15);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: newValue,
    }));

    // Validación en tiempo real
    if (newValue.trim().length > 0) {
      setErrors((prev) => ({
        ...prev,
        [name]: validateField(name, newValue),
      }));
    } else {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Limpiar mensaje anterior al modificar el formulario
    if (submitMessage.text) {
      setSubmitMessage({
        type: "",
        text: "",
      });
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (!file) {
      setFormData((prev) => ({
        ...prev,
        file: null,
      }));

      return;
    }

    // Validar tamaño máximo de 10MB
    if (file.size > 10 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        file: "El archivo no puede superar los 10MB",
      }));

      setFormData((prev) => ({
        ...prev,
        file: null,
      }));

      return;
    }

    setErrors((prev) => ({
      ...prev,
      file: "",
    }));

    setFormData((prev) => ({
      ...prev,
      file,
    }));

    // Limpiar mensaje anterior al seleccionar otro archivo
    if (submitMessage.text) {
      setSubmitMessage({
        type: "",
        text: "",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Limpiar mensaje anterior
    setSubmitMessage({
      type: "",
      text: "",
    });

    // Validar formulario
    const validationErrors = validateForm(formData);

    setErrors(validationErrors);

    // Detener el envío si existen errores
    if (Object.keys(validationErrors).length > 0) {
      return;
    }

    try {
      // Activar estado de envío
      setIsSending(true);

      // Mostrar SweetAlert mientras se envía la postulación
      Swal.fire({
        title: "Enviando postulación",
        text: "Por favor espera un momento mientras procesamos tu información...",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showConfirmButton: false,
        background: "#ffffff",
        color: "#333333",
        customClass: {
          popup: "rounded-3xl",
        },
        didOpen: () => {
          Swal.showLoading();
        },
      });

      // Enviar formulario al backend PHP
      const response = await sendWorkWithUsApplication(formData);

      // Verificar respuesta del servidor
      if (!response || response.success !== true) {
        throw new Error(
          response?.message ||
            "No fue posible enviar tu postulación. Por favor, inténtalo nuevamente.",
        );
      }

      // Mostrar SweetAlert de éxito
      await Swal.fire({
        icon: "success",
        title: "¡Postulación enviada!",
        text: "Tu hoja de vida fue enviada correctamente. Gracias por tu interés en trabajar con nosotros.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ee7d32",
        background: "#ffffff",
        color: "#333333",
        customClass: {
          popup: "rounded-3xl",
          confirmButton: "rounded-xl px-6 py-3",
        },
      });

      // Mostrar también mensaje interno de éxito
      setSubmitMessage({
        type: "success",
        text: "Tu postulación fue enviada correctamente. Gracias por tu interés en trabajar con nosotros.",
      });

      // Limpiar formulario
      setFormData({
        name: "",
        phone: "",
        email: "",
        position: "",
        message: "",
        file: null,
      });

      // Limpiar errores
      setErrors({});

      // Limpiar input de archivo
      const fileInput = document.querySelector('input[type="file"]');

      if (fileInput) {
        fileInput.value = "";
      }
    } catch (error) {
      console.error("Error al enviar postulación:", error);

      // Cerrar cualquier SweetAlert de carga
      Swal.close();

      // Mostrar mensaje interno de error
      setSubmitMessage({
        type: "error",
        text:
          error.message ||
          "Ocurrió un error al enviar tu postulación. Por favor, inténtalo nuevamente.",
      });

      // Mostrar SweetAlert de error
      await Swal.fire({
        icon: "error",
        title: "No se pudo enviar",
        text:
          error.message ||
          "Ocurrió un error al enviar tu postulación. Por favor, inténtalo nuevamente.",
        confirmButtonText: "Aceptar",
        confirmButtonColor: "#ee7d32",
        background: "#ffffff",
        color: "#333333",
        customClass: {
          popup: "rounded-3xl",
          confirmButton: "rounded-xl px-6 py-3",
        },
      });
    } finally {
      setIsSending(false);
    }
  };
  return (
    <section className="min-h-screen bg-gradient-to-b from-[#f8f6f3] via-white to-[#f4f7fb] py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 overflow-hidden rounded-[36px] bg-white shadow-[0_30px_80px_rgba(0,0,0,.08)]">
          {/* PANEL VISUAL */}
          <div className="relative min-h-[700px] overflow-hidden">
            <img
              src="/Images/work-with-us/work-with-us.webp"
              alt="Trabaja con nosotros"
              className="absolute inset-0 w-full h-full object-cover"
            />

            <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/65 to-black/40" />

            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ee7d32]/5 rounded-full blur-[140px]" />

            <div className="relative z-10 h-full flex items-center p-10 lg:p-16">
              <div className="max-w-lg">
                <span
                  className="
                    inline-flex items-center gap-2
                    px-3 py-1.5 md:px-4 md:py-2
                    rounded-full
                    bg-white/10
                    backdrop-blur-md
                    border border-white/10
                    text-[#ee7d32]
                    font-semibold
                    tracking-widest
                    uppercase
                    text-[10px] md:text-xs
                  "
                >
                  Talento Humano
                </span>

                <h1
                  className="
                    mt-4 md:mt-6
                    text-3xl md:text-4xl lg:text-6xl
                    font-bold
                    text-white
                    leading-tight
                  "
                >
                  Construye tu futuro con nosotros
                </h1>

                <p className="hidden md:block mt-6 text-lg text-white leading-relaxed">
                  Estamos en búsqueda de profesionales comprometidos,
                  innovadores y apasionados por generar impacto.
                </p>

                <div className="mt-10 space-y-5">
                  {[
                    "Oportunidades de crecimiento profesional",
                    "Excelente ambiente laboral",
                    "Proyectos de alto impacto",
                  ].map((item) => (
                    <div
                      key={item}
                      className="flex items-center gap-3 text-white"
                    >
                      <CheckCircle
                        size={20}
                        stroke="white"
                        className="shrink-0"
                      />

                      <span className="text-white">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* FORMULARIO */}
          <div className="relative p-8 lg:p-14">
            <div className="absolute top-0 right-0 w-72 h-72 bg-[#ee7d32]/5 rounded-full blur-[120px]" />

            <div className="relative z-10">
              <h2 className="text-3xl font-bold text-gray-900">
                Envía tu Hoja de Vida
              </h2>

              <p className="mt-3 text-gray-600">
                Completa la información y nuestro equipo evaluará tu perfil.
              </p>

              <form onSubmit={handleSubmit} className="mt-10 space-y-6">
                {/* NOMBRE Y TELÉFONO */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <div className="relative">
                      <User
                        size={18}
                        className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        autoComplete="off"
                        placeholder="Nombre completo"
                        className={`border border-solid
                          w-full pl-14 pr-6 py-5
                          bg-white rounded-3xl shadow-sm outline-none transition-all
                          ${errors.name ? "border-red-400" : "border-gray-200"}
                          focus:border-[#ee7d32]
                          focus:ring-2 focus:ring-[#ee7d32]/10
                        `}
                      />
                    </div>

                    {errors.name && (
                      <p className="mt-2 text-sm text-red-500">{errors.name}</p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <Phone
                        size={18}
                        className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        type="text"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        autoComplete="off"
                        placeholder="Teléfono"
                        className={`border border-solid
                          w-full pl-14 pr-6 py-5
                          bg-white rounded-3xl shadow-sm outline-none transition-all
                          ${errors.phone ? "border-red-400" : "border-gray-200"}
                          focus:border-[#ee7d32]
                          focus:ring-2 focus:ring-[#ee7d32]/10
                        `}
                      />
                    </div>

                    {errors.phone && (
                      <p className="mt-2 text-sm text-red-500">
                        {errors.phone}
                      </p>
                    )}
                  </div>
                </div>

                {/* CORREO Y CARGO */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <div className="relative">
                      <Mail
                        size={18}
                        className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        autoComplete="off"
                        placeholder="Correo electrónico"
                        className={`border border-solid
                          w-full pl-14 pr-6 py-5
                          bg-white rounded-3xl shadow-sm outline-none transition-all
                          ${errors.email ? "border-red-400" : "border-gray-200"}
                          focus:border-[#ee7d32]
                          focus:ring-2 focus:ring-[#ee7d32]/10
                        `}
                      />
                    </div>

                    {errors.email && (
                      <p className="mt-2 text-sm text-red-500">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <div className="relative">
                      <Briefcase
                        size={18}
                        className="absolute left-5 top-1/2 -translate-y-1/2 text-gray-400"
                      />

                      <input
                        type="text"
                        name="position"
                        value={formData.position}
                        onChange={handleChange}
                        autoComplete="off"
                        placeholder="Cargo al que aplicas"
                        className={`border border-solid
                          w-full pl-14 pr-6 py-5
                          bg-white rounded-3xl shadow-sm outline-none transition-all
                          ${
                            errors.position
                              ? "border-red-400"
                              : "border-gray-200"
                          }
                          focus:border-[#ee7d32]
                          focus:ring-2 focus:ring-[#ee7d32]/10
                        `}
                      />
                    </div>

                    {errors.position && (
                      <p className="mt-2 text-sm text-red-500">
                        {errors.position}
                      </p>
                    )}
                  </div>
                </div>

                {/* MENSAJE */}
                <div>
                  <div className="relative">
                    <MessageSquare
                      size={18}
                      className="absolute left-5 top-5 text-gray-400"
                    />

                    <textarea
                      rows={5}
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Cuéntanos sobre tu experiencia y perfil profesional..."
                      className={`border border-solid
                        w-full pl-14 pr-6 py-5
                        bg-white rounded-3xl shadow-sm resize-none
                        outline-none transition-all
                        ${errors.message ? "border-red-400" : "border-gray-200"}
                        focus:border-[#ee7d32]
                        focus:ring-2 focus:ring-[#ee7d32]/10
                      `}
                    />
                  </div>

                  {errors.message && (
                    <p className="mt-2 text-sm text-red-500">
                      {errors.message}
                    </p>
                  )}
                </div>

                {/* ARCHIVO */}
                <label
                  className="
                    group
                    flex flex-col items-center justify-center gap-4
                    w-full p-10
                    rounded-3xl
                    border-2 border-dashed border-gray-300
                    bg-white
                    cursor-pointer
                    transition-all duration-300
                    hover:border-[#ee7d32]
                    hover:bg-[#fff7f2]
                  "
                >
                  <div
                    className="
                      w-16 h-16
                      rounded-2xl
                      bg-orange-100
                      flex items-center justify-center
                      transition-all duration-300
                      group-hover:scale-110
                    "
                  >
                    <UploadCloud size={30} className="text-[#ee7d32]" />
                  </div>

                  <div className="text-center">
                    <p className="font-medium text-gray-700">
                      Adjunta tu hoja de vida
                    </p>

                    <p className="text-sm text-gray-500 mt-1">
                      PDF, DOC o DOCX · Máximo 10MB
                    </p>
                  </div>

                  <span className="text-[#ee7d32] font-semibold text-sm">
                    {formData.file ? formData.file.name : "Seleccionar archivo"}
                  </span>

                  <input
                    type="file"
                    className="hidden"
                    accept=".pdf,.doc,.docx"
                    onChange={handleFileChange}
                  />
                </label>

                {errors.file && (
                  <p className="mt-2 text-sm text-red-500">{errors.file}</p>
                )}

                <p className="text-sm text-gray-500 leading-relaxed">
                  Al enviar este formulario aceptas el tratamiento de tus datos
                  personales conforme a nuestra{" "}
                  <Link
                    to="/policies"
                    className="text-[#ee7d32] font-medium hover:underline"
                  >
                    Política de Privacidad
                  </Link>
                  .
                </p>

                {/* MENSAJE DE RESPUESTA */}
                {submitMessage.text && (
                  <div
                    className={`rounded-2xl px-5 py-4 text-sm font-medium ${
                      submitMessage.type === "success"
                        ? "border border-green-200 bg-green-50 text-green-700"
                        : "border border-red-200 bg-red-50 text-red-700"
                    }`}
                  >
                    {submitMessage.text}
                  </div>
                )}

                {/* BOTÓN */}
                <button
                  type="submit"
                  disabled={isSending}
                  className="
                    group
                    w-full
                    flex items-center justify-center gap-3
                    px-8 py-5
                    rounded-3xl
                    bg-gradient-to-r
                    from-[#ee7d32]
                    to-[#ff9d58]
                    text-white
                    font-semibold
                    shadow-[0_20px_40px_rgba(238,125,50,.25)]
                    hover:shadow-[0_30px_60px_rgba(238,125,50,.35)]
                    hover:scale-[1.01]
                    transition-all duration-300
                    disabled:opacity-60
                    disabled:cursor-not-allowed
                    disabled:hover:scale-100
                  "
                >
                  {isSending ? "Enviando postulación..." : "Enviar Postulación"}

                  {!isSending && (
                    <ArrowRight
                      size={18}
                      stroke="white"
                      className="
                        transition-transform duration-300
                        group-hover:translate-x-2
                      "
                    />
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default WorkWithUs;
